import type { LoanSchedulePeriod } from '@src/types/OTC/loanSchedulePeriod';
import { LoanPaymentTypeEnum } from '@talos/kyoko';
import { useCallback, useRef } from 'react';
import {
  applyLoanPaymentTypeChange,
  DEFAULT_REMEMBERED_PERIODIC_PERIODS,
  type RememberedPeriodicPeriods,
} from './loanPaymentType';

export type LoanPaymentTypeFieldChange = (
  field: 'loanPaymentType' | 'paymentPeriod' | 'accrualPeriod',
  value: string
) => void;

export function useLoanPaymentTypeChange(
  handleFieldChange: LoanPaymentTypeFieldChange,
  current: {
    loanPaymentType?: LoanPaymentTypeEnum;
    paymentPeriod?: LoanSchedulePeriod;
    accrualPeriod?: LoanSchedulePeriod;
  }
): (nextPaymentType: LoanPaymentTypeEnum) => void {
  const rememberedPeriodicPeriodsRef = useRef<RememberedPeriodicPeriods>(DEFAULT_REMEMBERED_PERIODIC_PERIODS);

  return useCallback(
    (nextPaymentType: LoanPaymentTypeEnum) => {
      const currentPaymentType = current.loanPaymentType ?? LoanPaymentTypeEnum.Periodic;
      if (nextPaymentType === currentPaymentType) {
        return;
      }

      const result = applyLoanPaymentTypeChange({
        nextPaymentType,
        currentPaymentPeriod: current.paymentPeriod,
        currentAccrualPeriod: current.accrualPeriod,
        rememberedPeriodicPeriods: rememberedPeriodicPeriodsRef.current,
      });
      rememberedPeriodicPeriodsRef.current = result.rememberedPeriodicPeriods;
      handleFieldChange('loanPaymentType', result.loanPaymentType);
      handleFieldChange('paymentPeriod', result.paymentPeriod);
      handleFieldChange('accrualPeriod', result.accrualPeriod);
    },
    [current.accrualPeriod, current.loanPaymentType, current.paymentPeriod, handleFieldChange]
  );
}
